import { createFileRoute, useNavigate } from "@tanstack/react-router"
import * as React from "react"

import { ValidationFailed } from "@/api/client"
import { Button, Card, CardContent, Field, Input, SignageBand } from "@/components/ui"
import { INSTANCE } from "@/lib/instance"
import { useLogin, useSession } from "@/lib/session"

export const Route = createFileRoute("/login")({ component: LoginScreen })

function LoginScreen() {
  const navigate = useNavigate()
  const login = useLogin()
  const { me } = useSession()
  const [username, setUsername] = React.useState("")
  const [password, setPassword] = React.useState("")
  const [error, setError] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (me) void navigate({ to: "/", replace: true })
  }, [me, navigate])

  function onSubmit(event: React.FormEvent) {
    event.preventDefault()
    setError(null)
    login.mutate(
      { username, password },
      {
        onSuccess: () => void navigate({ to: "/", replace: true }),
        onError: (cause) => {
          // The server decides. Show what it said rather than guessing which
          // field was wrong, because it deliberately does not say.
          setError(
            cause instanceof ValidationFailed
              ? cause.message
              : "That username and password did not work.",
          )
        },
      },
    )
  }

  return (
    <div className="brand-flow grid min-h-dvh place-items-center bg-background p-4">
      {/* The card wears the same rim-ruled band a dialog does, at the dialog
          scale, with "Sign in" as the band's own title rather than a heading
          repeated under a decorative strip. `--column-inset` is this card's
          own `p-6`, so the title starts where the form's labels start. */}
      <Card className="rise-in w-full max-w-sm overflow-hidden [--column-inset:1.5rem]">
        <SignageBand title="Sign in" size="dialog" />
        <CardContent className="p-6">
          <div className="mb-5 flex items-center gap-2">
            <span className="brand-rule" aria-hidden />
            <span className="section-label">{INSTANCE.name}</span>
          </div>
          <form onSubmit={onSubmit} noValidate>
            <Field label="Username" htmlFor="username" required>
              <Input
                id="username"
                value={username}
                onChange={(event) => setUsername(event.target.value)}
                autoComplete="username"
                autoFocus
                required
              />
            </Field>
            <Field label="Password" htmlFor="password" required error={error}>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="current-password"
                required
              />
            </Field>
            <Button type="submit" className="w-full" loading={login.isPending}>
              Sign in
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
